import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SipService, CallState, ConnectionStatus } from '@services/sip.service';
import { Router } from '@angular/router';
import { SipModule } from '@pages/sip/sip.module';

@Component({
  selector: 'app-global-sip-call',
  templateUrl: './global-sip-call.component.html',
  styleUrls: ['./global-sip-call.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, TranslateModule, SipModule]
})
export class GlobalSipCallComponent implements OnInit, OnDestroy {
  // 🔥 Estados para mostrar las llamadas globalmente
  calls: CallState[] = [];
  connectionStatus: ConnectionStatus = 'disconnected';

  // 🔥 Ya no necesitamos rastrear números localmente, el SipService lo hace centralmente
  private subscriptions = new Subscription();

  constructor(
    private sip: SipService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    this.initializeSubscriptions();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private initializeSubscriptions() {
    // Suscripción a las actualizaciones de llamadas
    const callsUpdateSub = this.sip.callsUpdate$.subscribe(async (calls) => {
      this.ngZone.run(() => {
        this.calls = calls;
        // 🔥 Los datos de usuario se obtienen automáticamente en SipService
        this.cdr.detectChanges();
      });
    });

    // Suscripción al estado de la conexión
    const connSub = this.sip.connectionState$.subscribe(status => {
      this.ngZone.run(() => {
        this.connectionStatus = status;
        console.log('🔗 [GlobalSipCall] Estado de conexión:', status);

        // 🔥 CRÍTICO: NUNCA limpiar llamadas si hay auto-conexión activa
        // Solo limpiar si desconexión definitiva Y auto-conexión deshabilitada
        if (status === 'disconnected' && !this.sip.isAutoConnectActive()) {
          console.log('🔥 [GlobalSipCall] Limpiando llamadas (desconexión definitiva)');
          this.calls = [];
        } else {
          console.log('🔥 [GlobalSipCall] Manteniendo llamadas (auto-reconexión activa o estado temporal)');
        }

        this.cdr.detectChanges();
      });
    });

    // 🔥 Suscripción a actualizaciones de datos de usuario (centralizado en SipService)
    const userDataSub = this.sip.userDataUpdate$.subscribe(() => {
      this.ngZone.run(() => {
        // Cuando hay actualizaciones de datos de usuario, actualizar la UI
        this.cdr.detectChanges();
      });
    });

    this.subscriptions.add(callsUpdateSub);
    this.subscriptions.add(connSub);
    this.subscriptions.add(userDataSub);
  }

  answer(callId: string) {
    this.sip.answer(callId).then(() => {
      this.cdr.detectChanges();
    });
  }

  hangup(callId: string) {
    this.sip.hangup(callId).then(() => {
      this.cdr.detectChanges();
    });
  }

  hold(callId: string) {
    this.sip.holdCall(callId).then(() => {
      this.cdr.detectChanges();
    });
  }

  resume(callId: string) {
    this.sip.resumeCall(callId).then(() => {
      this.cdr.detectChanges();
    });
  }

  // Helper para obtener el tipo de la llamada
  getCallType(call: CallState): 'ringing' | 'inCall' | 'onHold' {
    if (call.state === 'ringing') return 'ringing';
    if (call.state === 'active') return 'inCall';
    return 'onHold';
  }

  // Helper para obtener el objeto completo de datos de usuario para una llamada
  getUserDataForCall(call: CallState) {
    return this.sip.getUserData(call.callerId);
  }

  // TrackBy function para optimizar el ngFor
  trackByCallId(index: number, call: CallState): string {
    return call.id;
  }

  // 🔥 Navegar a la página SIP para gestión completa
  goToSipPage() {
    this.router.navigate(['/sip']);
  }

  // 🔥 Atajos de teclado globales
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // ⚠️ IMPORTANTE: Ignorar atajos de teclado cuando el usuario está escribiendo
    // Esto previene que se disparen acciones mientras se escribe en inputs/textareas
    if (this.isUserTyping(event)) {
      return;
    }

    // No hacer nada si no hay conexión
    if (this.connectionStatus !== 'connected') {
      return;
    }

    switch (event.key.toLowerCase()) {
      case ' ':
        // Espacio: Contestar la primera llamada entrante solo si NO hay ninguna llamada contestada
        if (this.handleSpaceKey()) {
          event.preventDefault(); // Prevenir scroll de página solo si se contestó una llamada
        }
        break;
      case 'c':
        // C: Colgar la llamada activa solo si hay una llamada en curso
        if (this.handleCKey()) {
          event.preventDefault(); // Prevenir comportamiento por defecto solo si se colgó una llamada
        }
        break;
    }
  }

  /**
   * Maneja la tecla Espacio: Contesta la primera llamada entrante si no hay ninguna llamada activa
   * @returns true si se contestó una llamada, false en caso contrario
   */
  private handleSpaceKey(): boolean {
    // Verificar si hay alguna llamada activa (inCall)
    const activeCall = this.sip.getActiveCall();
    if (activeCall) {
      // Ya hay una llamada contestada, no hacer nada
      return false;
    }

    // Buscar la primera llamada entrante (ringing) más antigua
    const ringingCalls = this.calls.filter(call => call.state === 'ringing');
    if (ringingCalls.length === 0) {
      // No hay llamadas entrantes
      return false;
    }

    // Ordenar por startTime si está disponible, sino por orden de llegada (primera del array)
    const sortedCalls = ringingCalls.sort((a, b) => {
      if (a.startTime && b.startTime) {
        return a.startTime.getTime() - b.startTime.getTime();
      }
      // Si no tienen startTime, mantener el orden original (primera = más antigua)
      return 0;
    });

    const oldestRingingCall = sortedCalls[0];
    this.answer(oldestRingingCall.id);
    return true;
  }

  /**
   * Maneja la tecla C: Cuelga la llamada activa solo si hay una en curso
   * @returns true si se colgó una llamada, false en caso contrario
   */
  private handleCKey(): boolean {
    const activeCall = this.sip.getActiveCall();
    if (!activeCall) {
      // No hay llamada activa, no hacer nada
      return false;
    }

    // Colgar la llamada activa
    this.hangup(activeCall.id);
    return true;
  }

  /**
   * Verifica si el usuario está escribiendo en un campo de entrada
   * @param event Evento del teclado
   * @returns true si el usuario está en un input, textarea o elemento editable
   */
  private isUserTyping(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();

    // Verificar si es un campo de entrada estándar
    if (tagName === 'input' || tagName === 'textarea') {
      return true;
    }

    // Verificar si es un elemento contenteditable
    if (target.isContentEditable) {
      return true;
    }

    // Verificar si está dentro de un elemento con role="textbox"
    if (target.getAttribute('role') === 'textbox') {
      return true;
    }

    // Verificar si está dentro de un contenedor de Google Autocomplete
    // Los inputs de Google Places están dentro de gmp-place-autocomplete con Shadow DOM
    let currentElement: HTMLElement | null = target;
    while (currentElement) {
      // Verificar si el elemento está dentro de un contenedor con clase google-autocomplete-container
      if (currentElement.classList?.contains('google-autocomplete-container')) {
        return true;
      }

      // Verificar si es un elemento gmp-place-autocomplete (Web Component de Google Places)
      const currentTagName = currentElement.tagName?.toLowerCase();
      if (currentTagName === 'gmp-place-autocomplete') {
        return true;
      }

      // Verificar si está dentro del Shadow DOM de un gmp-place-autocomplete
      // Si el elemento está dentro de un Shadow Root, verificar el host
      if (currentElement.getRootNode && currentElement.getRootNode() instanceof ShadowRoot) {
        const shadowRoot = currentElement.getRootNode() as ShadowRoot;
        const host = shadowRoot.host;
        if (host.tagName?.toLowerCase() === 'gmp-place-autocomplete') {
          return true;
        }
        // Si es un input dentro de cualquier Shadow DOM, también es un campo de entrada
        if (currentTagName === 'input') {
          return true;
        }
      }

      // Moverse al elemento padre
      currentElement = currentElement.parentElement;
    }

    return false;
  }
}
